// ============================================================
// PTUTOR DRAWER (p2026 #613)
// Right-side panel that embeds the PTutor page (WordPress) in an iframe.
// Replaces the old chatPythaverse.php popup.
//
// - Identity comes from the WordPress session cookie inside the iframe.
//   The IDE never passes a username.
// - IDE <-> PTutor only talk through postMessage: always sent with an explicit
//   targetOrigin, and only accepted from the PTutor iframe (origin AND source).
// ============================================================

const PTUTOR_URL_PROD = "https://pythaverse.space/ptutor/?embed=1";
const PTUTOR_URL_QA   = "https://qa.pythaverse.space/ptutor/?embed=1";

// ide2-qa.pythaverse.space -> QA PTutor, everything else -> PROD PTutor
export const PTUTOR_URL    = /qa/i.test(window.location.hostname) ? PTUTOR_URL_QA : PTUTOR_URL_PROD;
export const PTUTOR_ORIGIN = new URL(PTUTOR_URL).origin;

const LS_KEY_OPEN = "ptutor.open";

// Open UI that already uses Esc itself (Monaco suggest/find widget, ideUI modal)
const ESC_OWNERS = ".monaco-editor .editor-widget.visible, dialog[open]";

export class PTutorDrawer {

  /** Callbacks (wired in main.js) */
  getContext = () => ({ code: "", language: "cpp" }); // code the student currently sees
  insertCode = (code, language) => false;             // true = code went into the text editor
  onCopied   = (ok) => {};                            // insert fell back to the clipboard

  #el = {};               // drawer, workspace, toggleButton, frameHost
  #frame = null;          // PTutor iframe, created on first open
  #isReady = false;       // PTutor sent 'ready'
  #pendingAction = null;  // action clicked before PTutor was ready

  /* ================= PUBLIC API ================= */

  attach({ drawer, workspace, toggleButton, frameHost, btnExplain, btnDebug, btnClose }) {
    this.#el = { drawer, workspace, toggleButton, frameHost };

    toggleButton.addEventListener("click", () => this.toggle());
    btnClose.addEventListener("click", () => this.close());
    btnExplain.addEventListener("click", () => this.sendAction("explain"));
    btnDebug.addEventListener("click", () => this.sendAction("debug"));

    window.addEventListener("message", (e) => this.#onMessage(e));

    // capture phase: runs before Monaco hides its own suggest/find widget on the same Esc
    window.addEventListener("keydown", (e) => {
      if (e.key !== "Escape" || !this.isOpen()) return;
      if (document.querySelector(ESC_OWNERS)) return; // that Esc belongs to the open widget/modal
      this.close();
    }, true);

    if (localStorageGet(LS_KEY_OPEN) === "true") this.open();
  }

  isOpen() {
    return !this.#el.drawer.classList.contains("is-hidden");
  }

  toggle() {
    this.isOpen() ? this.close() : this.open();
  }

  open() {
    this.#ensureFrame();
    this.#el.drawer.classList.remove("is-hidden");
    this.#el.workspace.classList.add("ptutor-open"); // editor shrinks, Monaco automaticLayout follows
    this.#el.toggleButton.classList.add("active");
    localStorageSet(LS_KEY_OPEN, "true");
  }

  close() {
    this.#el.drawer.classList.add("is-hidden"); // iframe stays loaded, chat survives close/open
    this.#el.workspace.classList.remove("ptutor-open");
    this.#el.toggleButton.classList.remove("active");
    localStorageSet(LS_KEY_OPEN, "false");
  }

  sendAction(action) { // "explain" | "debug"
    this.open();
    if (this.#isReady) this.#sendContext(action);
    else this.#pendingAction = action; // sent on 'ready'
  }

  /* ================= INTERNAL ================= */

  #ensureFrame() {
    if (this.#frame) return;

    const frame = document.createElement("iframe");
    frame.id = "ptutorFrame";
    frame.title = "PTutor";
    frame.allow = "clipboard-write";
    frame.src = PTUTOR_URL;

    this.#el.frameHost.appendChild(frame);
    this.#frame = frame;
  }

  #sendContext(action) {
    if (!this.#frame?.contentWindow) return;

    const { code, language } = this.getContext();
    this.#frame.contentWindow.postMessage({
      source: "ptutor-host",
      type: "context",
      code: String(code ?? ""),
      language,
      action: action ?? null,
    }, PTUTOR_ORIGIN);
  }

  #onMessage(event) {
    if (!this.#frame || event.origin !== PTUTOR_ORIGIN || event.source !== this.#frame.contentWindow) return;

    const msg = event.data;
    if (!msg || typeof msg !== "object" || msg.source !== "ptutor") return;

    switch (msg.type) {
      case "ready":
        this.#isReady = true;
        this.#sendContext(this.#pendingAction);
        this.#pendingAction = null;
        break;

      case "request-context":
        this.#sendContext(null);
        break;

      case "insert":
        if (typeof msg.code === "string") this.#insert(msg.code, msg.language);
        break;

      default: // anything else is ignored
        break;
    }
  }

  async #insert(code, language) {
    if (this.insertCode(code, language)) return;

    // Blockly / read-only view: no text cursor to insert at -> clipboard
    this.onCopied(await copyToClipboard(code));
  }
}

/* ================= HELPERS ================= */

// The click that sends 'insert' happens inside the iframe, so focus a textarea in
// this document first: the Clipboard API refuses a document that has no focus.
async function copyToClipboard(text) {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.focus();
  ta.select();

  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      return document.execCommand("copy"); // older browsers / clipboard permission denied
    } catch {
      return false;
    }
  } finally {
    ta.remove();
  }
}

function localStorageGet(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}

function localStorageSet(key, value) {
  try { localStorage.setItem(key, value); } catch { /* private mode: state just isn't remembered */ }
}
